import React, { useEffect } from 'react';
import { ArrowForward } from '@mui/icons-material';
import { format12HourTime } from 'shared/Dates';
import { useClient } from 'shared';
import { Colors } from 'library';
import { Chip, Icon, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import type { WorkerInitiatedAppointmentTimeChangeRequest } from 'models';

export function TimeChangeModal({
    open,
    setOpen,
    request,
    refresh,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    request: WorkerInitiatedAppointmentTimeChangeRequest;
    refresh: () => void;
}) {
    const { loading, finished, error, send } = useClient({
        url: `api/worker-initiated-appointment-time-change-request/${request.id}/`,
        method: 'PATCH',
        options: { sendImmediately: false },
    });

    function click(choice: 'accepted' | 'declined') {
        send({ body: { status: choice } });
    }

    useEffect(() => {
        if (finished && !error) {
            setOpen(false);
            refresh();
        }
    }, [finished, error, setOpen, refresh]);

    if (!open) return null;

    return (
        <Modal title="Time Change Request" onClose={() => setOpen(false)}>
            <Stack gap="md">
                <Row justify="between" align="center" fullWidth>
                    <Text>The worker has requested the following time change:</Text>
                    <Chip variant="filled" bgColor={Colors.accent} textColor={Colors.white}>
                        {request.status}
                    </Chip>
                </Row>
                <Row justify="around" align="center" fullWidth>
                    <Stack gap="none">
                        <Text>From:</Text>
                        <Text weight="bold">
                            {`${format12HourTime(request.old_start_time)} - ${format12HourTime(request.old_end_time)}`}
                        </Text>
                    </Stack>
                    <Icon icon={ArrowForward} size="sm" bgColor={Colors.accentSoft} iconColor={Colors.primary} />
                    <Stack gap="none">
                        <Text>To:</Text>
                        <Text weight="bold">
                            {`${format12HourTime(request.new_start_time)} - ${format12HourTime(request.new_end_time)}`}
                        </Text>
                    </Stack>
                </Row>
                <Text size="sm">
                    If you decline this request, the worker will be{' '}
                    {request.on_decline === 'keep_provider' ? (
                        <Text size="sm" weight="bold">
                            kept on this shift
                        </Text>
                    ) : (
                        <Text size="sm">
                            <Text size="sm" weight="bold">
                                removed from this shift
                            </Text>
                            , and we will work to refill the original times.
                        </Text>
                    )}
                    {request.on_decline === 'remove_provider' && (
                        <Text size="sm" tone="error">
                            You will not be charged for a late cancellation.
                        </Text>
                    )}
                </Text>
                {error && (
                    <Text size="sm" tone="error">
                        There was an error trying to update this time change request. Please try again or contact
                        support.
                    </Text>
                )}
                <Row justify="between" fullWidth>
                    <PillButton variant="secondary" disabled={loading} onClick={() => click('declined')}>
                        Decline
                    </PillButton>
                    <PillButton disabled={loading} onClick={() => click('accepted')}>
                        Accept
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
